import React from "react";
import { imgUrl } from "../../lib/api";

/**
 * Live hero preview rendered alongside the AdminBranding form. Mirrors
 * the storefront's hero markup at small scale so tenants see exactly
 * what their customers will see — without saving + reloading the
 * storefront iframe on every keystroke.
 *
 * The fallback copy mirrors KalkiHero's curated defaults so admins
 * always see *something* in the preview, even with all fields blank.
 */
const FALLBACK_BG = "https://images.pexels.com/photos/33101418/pexels-photo-33101418.jpeg";

function renderTitle(title) {
  if (!title) return null;
  const lines = title.split(/\r?\n/);
  return lines.map((line, i) => {
    const parts = line.split(/\*([^*]+)\*/g);
    return (
      <React.Fragment key={i}>
        {parts.map((p, j) =>
          j % 2 === 1
            ? <em key={j} className="italic" style={{ color: "var(--accent)" }}>{p}</em>
            : <React.Fragment key={j}>{p}</React.Fragment>
        )}
        {i < lines.length - 1 && <br />}
      </React.Fragment>
    );
  });
}

export default function HeroLivePreview({ form, accentColor = "#7c3a4b" }) {
  const eyebrow = form.hero_eyebrow || form.tagline || "The Bridal Edit · Autumn–Winter";
  const titleText = form.hero_title || "Worn by stories.\nWoven *by hands*.";
  const subtitle = form.hero_subtitle || "A marketplace for India's finest independent ateliers — from Banarasi looms to Chikankari studios.";
  const cta1 = form.hero_cta_primary_label || "Explore the Bazaar";
  const cta2 = form.hero_cta_secondary_label || "Sell Your Craft";
  const heroImg = (form.hero_image_url || "").trim() ? imgUrl(form.hero_image_url) : FALLBACK_BG;

  return (
    <div className="space-y-3" data-testid="hero-live-preview">
      <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--kb-muted))] flex items-center justify-between">
        <span>Live preview</span>
        <span className="opacity-60">Updates as you type</span>
      </div>
      {/* Phone-sized frame, mirrors a real device aspect */}
      <div
        className="relative w-full overflow-hidden rounded-[20px] border-[6px] border-black shadow-lg"
        style={{ aspectRatio: "9 / 16", "--accent": accentColor }}
      >
        {/* Notch */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-20 h-4 bg-black rounded-b-xl z-30" />
        <img
          src={heroImg}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          onError={(e) => { e.currentTarget.src = FALLBACK_BG; }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/25 to-black/65" />
        <div className="relative z-10 h-full flex flex-col items-center justify-center text-center text-white px-4">
          <div className="text-[8px] uppercase tracking-[0.25em] text-white/90 mb-3 line-clamp-2">{eyebrow}</div>
          <div
            className="text-[22px] leading-[0.95] font-light tracking-tight"
            style={{ fontFamily: "var(--kb-display-font, serif)" }}
          >
            {renderTitle(titleText)}
          </div>
          <p className="text-[9px] text-white/85 leading-snug mt-3 max-w-[90%] line-clamp-3">{subtitle}</p>
          <div className="flex gap-1.5 mt-4 flex-wrap justify-center">
            <span
              className="px-3 py-1.5 text-[9px] uppercase tracking-wider font-semibold rounded-sm"
              style={{ background: accentColor, color: "white" }}
            >
              {cta1}
            </span>
            {cta2 && (
              <span className="border border-white text-white px-3 py-1.5 text-[9px] uppercase tracking-wider font-semibold rounded-sm">
                {cta2}
              </span>
            )}
          </div>
        </div>
      </div>
      <p className="text-[11px] text-[hsl(var(--kb-muted))] leading-relaxed">
        This is a simplified preview. Your selected theme determines the exact layout (Kalki = centered, Ajio = split, Mango = right-aligned, etc.). Save to see the real hero on your storefront.
      </p>
    </div>
  );
}
